import {CarOutlined, DownloadOutlined} from '@ant-design/icons';
import {Button, Modal} from 'antd';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import toast from 'react-hot-toast';

import logo from '../../../../../Assets/Logo3.png';

// ─── Constants ────────────────────────────────────────────────────────────────
const COMPANY = {
  name:    'SAMADHAN INDUSTRIES',
  address: 'Plot No.- F-1, MIDC, ISHWARPUR.',
  city:    'Tal.- Walwa, Dist.- Sangli-415409',
  state:   'State Code: MH (27)',
  email:   'samadhanindustriesgroup89@gmail.com',
  phone:   '8412912712 / 8485824712',
};

const fmt = iso =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });

// ─── Challan Template ─────────────────────────────────────────────────────────
const ChallanTemplate = ({challanData, printRef}) => {
  const {customer, transport, rows, invoiceDate} = challanData;

  const cell = (extra = {}) => ({
    border:   '1px solid #6682af',
    padding:  '3px 5px',
    fontSize: '10px',
    ...extra,
  });

  const headerCell = (extra = {}) => ({
    ...cell({background: '#d3dbe8', fontWeight: 'bold', ...extra}),
  });

  // Pad to at least 10 rows for the blank lines look
  const paddedRows = [
    ...rows,
    ...Array.from({length: Math.max(0, 10 - rows.length)}).map(() => null),
  ];

  return (
    <div
      ref={printRef}
      style={{
        fontFamily: '\'Times New Roman\', Times, serif',
        fontSize:   '10px',
        color:      '#000',
        background: '#fff',
        padding:    '4mm 6mm',
        width:      '140mm',
        minHeight:  '210mm',
        boxSizing:  'border-box',
        margin:     '0 auto',
      }}
    >
      {/* ── Header ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '4px'}}>
        <tbody>
          <tr>
            <td style={{verticalAlign: 'top', width: '60%', paddingRight: '6px'}}>
              <p style={{margin: 0, fontSize: '9px', fontWeight: 'bold'}}>From</p>
              <p style={{margin: '2px 0 0', fontWeight: 'bold', fontSize: '11px'}}>{COMPANY.name}</p>
              <p style={{margin: 0, fontSize: '9px'}}>{COMPANY.address}</p>
              <p style={{margin: 0, fontSize: '9px'}}>{COMPANY.city}</p>
              <p style={{margin: 0, fontSize: '9px'}}>{COMPANY.state}</p>
            </td>
            <td style={{verticalAlign: 'top', width: '40%', textAlign: 'right'}}>
              <img src={logo} alt={'Logo'} style={{width: '100px', height: 'auto'}} />
              <p style={{margin: '4px 0 0', fontSize: '14px', color: '#c2410c', fontWeight: 'bold', fontFamily: 'monospace'}}>
                Transport Challan
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Consignee + Challan Meta ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #3a5f98', marginBottom: '4px'}}>
        <tbody>
          <tr>
            {/* Consignee */}
            <td style={{...cell({verticalAlign: 'top', width: '50%', padding: '4px 6px'})}}>
              <p style={{margin: 0, fontSize: '9px', fontWeight: 'bold'}}>Consignee</p>
              <p style={{margin: '2px 0 0', fontWeight: 'bold', fontSize: '11px'}}>
                {(customer?.customerName || '').toUpperCase()}
              </p>
              {customer?.customerAddress && (
                <p style={{margin: '1px 0 0', fontSize: '9px'}}>{customer.customerAddress}</p>
              )}
              {customer?.customerMobile && (
                <p style={{margin: '1px 0 0', fontSize: '9px'}}>Mob: {customer.customerMobile}</p>
              )}
              {customer?.gstIn && (
                <p style={{margin: '1px 0 0', fontSize: '9px'}}>GSTIN: {customer.gstIn}</p>
              )}
            </td>
            {/* Meta */}
            <td style={{...cell({verticalAlign: 'top', width: '50%', padding: '4px 6px'})}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                  {[
                    ['Date',        invoiceDate || fmt(new Date().toISOString())],
                    ['Challan No.', transport?.challanNumber || '—'],
                    ['Destination', transport?.destination   || '—'],
                  ].map(([label, val]) => (
                    <tr key={label}>
                      <td style={{fontSize: '9px', fontWeight: 'bold', padding: '1px 0', whiteSpace: 'nowrap', width: '45%'}}>{label}</td>
                      <td style={{fontSize: '9px', padding: '1px 0'}}>: {val}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{fontSize: '9px', fontWeight: 'bold', padding: '1px 0'}}>Reverse Charge</td>
                    <td style={{fontSize: '9px', padding: '1px 0'}}>
                      : Y &nbsp;
                      <span style={{display: 'inline-block', width: '10px', height: '10px', border: '1px solid #555', verticalAlign: 'middle'}} />
                      &nbsp; N &nbsp;
                      <span style={{display: 'inline-block', width: '10px', height: '10px', border: '1px solid #555', verticalAlign: 'middle'}} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Products Table ── */}
      {/* Columns: Sr | Product Name | Bags Wt (kg) | Bags | Qty | Remark */}
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #c8a07a', marginBottom: '4px'}}>
        <thead>
          <tr style={{background: '#e9edf4'}}>
            {[
              {label: 'Sr',           w: '6%'},
              {label: 'Product Name', w: '38%'},
              {label: 'Bags Wt (kg)', w: '14%'},
              {label: 'Bags',         w: '10%'},
              {label: 'Weight',       w: '10%'},
              {label: 'Remark',       w: '22%'},
            ].map(({label, w}) => (
              <th key={label} style={{...headerCell({textAlign: 'center', width: w, padding: '3px 4px'})}}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paddedRows.map((row, idx) => (
            <tr key={idx} style={{height: '20px'}}>
              {/* Sr */}
              <td style={cell({textAlign: 'center'})}>
                {row ? idx + 1 : ''}
              </td>

              {/* Product Name — show productName field */}
              <td style={cell({textAlign: 'left', fontWeight: row ? 'bold' : 'normal'})}>
                {row ? (row.productName || row.productLabel || '') : ''}
              </td>

              {/* Bags Weight */}
              <td style={cell({textAlign: 'center'})}>
                {row ? (row.bagsWeight !== undefined && row.bagsWeight !== '' ? row.bagsWeight : '0') : ''}
              </td>

              {/* Bags */}
              <td style={cell({textAlign: 'center'})}>
                {row ? (row.bags !== undefined && row.bags !== '' ? row.bags : '0') : ''}
              </td>

              {/* Qty */}
              <td style={cell({textAlign: 'center'})}>
                {row ? (row.quantity !== undefined && row.quantity !== '' ? row.quantity : '0') : ''}
              </td>

              {/* Remark — blank editable-looking cell */}
              <td style={cell({textAlign: 'left'})}>
                
              </td>
            </tr>
          ))}
          {/* NO total row — removed as requested */}
        </tbody>
      </table>

      {/* ── Footer: Vehicle / Driver / Date ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #c8a07a', marginBottom: '3px'}}>
        <tbody>
          <tr>
            <td style={cell({width: '42%'})}>
              <span style={{fontSize: '9px', color: '#c8500a'}}>🚛</span>
              <strong style={{fontSize: '9px'}}> Vehicle No. : </strong>
              <span style={{fontSize: '9px', display: 'inline-block', minWidth: '60px'}}>
                {transport?.vehicleNumber || ''}
              </span>
            </td>
            <td style={cell({width: '42%'})}>
              <strong style={{fontSize: '9px'}}>Transport Name : </strong>
              <span style={{fontSize: '9px', display: 'inline-block', minWidth: '60px'}}>
                {transport?.transportName || ''}
              </span>
            </td>
            <td style={cell({width: '15%'})}>
              <strong style={{fontSize: '9px'}}>Time : </strong>
              <span style={{fontSize: '9px'}}>
                {new Date().toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit', hour12: true})}
              </span>
            </td>
          </tr>
          <tr>
            <td style={cell({width: '33%'})}>
              <strong style={{fontSize: '9px'}}>Driver Name : </strong>
              <span style={{fontSize: '9px', display: 'inline-block', minWidth: '50px'}}>
                {transport?.driverName || ''}
              </span>
            </td>
            <td style={cell({width: '33%'})}>
              <strong style={{fontSize: '9px'}}>Driver Mobile : </strong>
              <span style={{fontSize: '9px', display: 'inline-block', minWidth: '50px'}}>
                {transport?.driverMobile || ''}
              </span>
            </td>
            <td style={cell({width: '33%'})}>
              <strong style={{fontSize: '9px'}}>Date : </strong>
              <span style={{fontSize: '9px'}}>{invoiceDate || fmt(new Date().toISOString())}</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Transport / freight strip */}
      {(transport?.transportName || transport?.driverName) && (
        <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #c8a07a', marginBottom: '3px'}}>
          <tbody>
            <tr>
              {transport?.transportName && (
                <td style={cell({width: '50%', fontSize: '9px'})}>
                  <strong>Transport:</strong> {transport.transportName}
                </td>
              )}
              {transport?.driverName && (
                <td style={cell({width: '50%', fontSize: '9px'})}>
                  <strong>Driver:</strong> {transport.driverName}
                  {transport.driverMobile ? ` (${transport.driverMobile})` : ''}
                </td>
              )}
            </tr>
            {transport?.destination && (
              <tr>
                <td colSpan={2} style={cell({fontSize: '9px'})}>
                  <strong>Destination:</strong> {transport.destination}
                  {transport.freightCharge
                    ? `  |  Freight: ₹${transport.freightCharge} (${transport.freightPaid ? 'PAID' : 'TO PAY'})`
                    : ''}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Note + signatures */}
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #d3dbe8'}}>
        <tbody>
          <tr>
            <td colSpan={3} style={cell({fontSize: '8px', color: '#555'})}>
              Note: No complaints will be entertained if the same are not received
              within 24 hours of delivery. Please return the counter-copy signed.
            </td>
          </tr>
          <tr>
            <td style={cell({width: '34%', fontSize: '9px', paddingTop: '16px', textAlign: 'center'})}>
              <strong>Checked By</strong>
            </td>
            <td style={cell({width: '33%', fontSize: '9px', paddingTop: '16px', textAlign: 'center'})}>
              <strong>Receivers Sign.</strong>
            </td>
            <td style={cell({width: '33%', fontSize: '9px', paddingTop: '12px', textAlign: 'right'})}>
              <strong>For, {COMPANY.name}</strong>
              <p style={{margin: '20px 0 0', fontStyle: 'italic', fontSize: '8px'}}>Authorised Signatory</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

ChallanTemplate.propTypes = {
  challanData: PropTypes.object.isRequired,
  printRef:    PropTypes.object.isRequired,
};

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const TransportChalanPdf = ({open, onClose, challanData}) => {
  const printRef                      = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!challanData) {return null;}

  const challanNo = challanData.transport?.challanNumber || 'CHALLAN';

  const handleDownload = async () => {
    if (!printRef.current) {return;}
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin:      [5, 5, 5, 5],
          filename:    `Challan-${challanNo.replace(/\//g, '-')}.pdf`,
          image:       {type: 'jpeg', quality: 0.98},
          html2canvas: {scale: 2, useCORS: true, logging: false},
          jsPDF:       {unit: 'mm', format: 'a5', orientation: 'portrait'},
        })
        .from(printRef.current)
        .save();
      toast.success('Challan PDF downloaded!');
      onClose();
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={700}
      title={(
        <div className={'flex items-center gap-2'}>
          <CarOutlined className={'text-primary-500'} />
          <span className={'font-bold text-gray-800'}>
            Delivery Challan —{' '}
            <span className={'font-mono text-primary-600'}>{challanNo}</span>
          </span>
        </div>
      )}
      styles={{body: {maxHeight: '80vh', overflowY: 'auto', padding: '16px 20px'}}}
      footer={(
        <div className={'flex justify-end gap-3 py-1'}>
          <Button onClick={onClose}>Close</Button>
          <Button
            type={'primary'}
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            Download Challan PDF
          </Button>
        </div>
      )}
    >
      <div className={'border border-primary-200 rounded-lg overflow-hidden shadow-sm bg-white'}>
        <ChallanTemplate challanData={challanData} printRef={printRef} />
      </div>
    </Modal>
  );
};

TransportChalanPdf.propTypes = {
  open:        PropTypes.bool.isRequired,
  onClose:     PropTypes.func.isRequired,
  challanData: PropTypes.object,
};

export default TransportChalanPdf;

// ─── Standalone download button ───────────────────────────────────────────────
export const ChallanDownloadButton = ({challanData, disabled}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = () => {
    if (disabled || !challanData) {
      toast.error('Please fill customer details and add products first');
      return;
    }
    setModalOpen(true);
  };

  return (
    <>
      <Button
        icon={<CarOutlined />}
        onClick={handleOpen}
        disabled={disabled}
        className={'btn-primary'}
      >
        Preview &amp; Download Challan
      </Button>

      <TransportChalanPdf
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        challanData={challanData}
      />
    </>
  );
};

ChallanDownloadButton.propTypes = {
  challanData: PropTypes.object,
  disabled:    PropTypes.bool,
};
